import { AgentPresenceInfo, CallInfo, StationInfo } from './Five9Service'

export const FIVE9_PING_INTERVAL = 10000 // 10 s
const FIVE9_NUMBER_OF_SOCKET_LIMIT = 2

interface Five9MessageContext {
  eventId: string
  eventReason: string
}

interface Five9MessageData {
  context: Five9MessageContext
  payLoad: any
}

export enum Five9EventType {
  CALL_DELETED = '5',
  CALL_DELETED_ALT = '31',
  CALL_CREATED = '3',
  CALL_CREATED_ALT = '30',
  CALL_UPDATED = '4',
  STATION_UPDATED = '18',
  PRESENCE_UPDATED = '12',
}

export const ping = (socket: WebSocket) => {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send('ping')
  }
}

/**
 * Creates a function that when invoked awaits for a particular Five9 ws event
 * @param ws The socket to listen to
 * @param timeout Amount of miliseconds to wait before failing
 * @param timeoutMessage Error message on timeout
 * @returns Promise
 */
export const createEventAwaiter = (
  ws: WebSocket,
  filter: (message: Five9MessageData) => boolean,
  timeout = 10000,
  timeoutMessage = 'Timed out awaiting for ws event'
) => {
  let timeoutRef: NodeJS.Timeout
  let listener: (event: MessageEvent) => void
  let rejectPromise: (reason?: any) => void
  const promise = new Promise<Five9MessageData>((resolve, reject) => {
    rejectPromise = reject
    listener = (event: MessageEvent) => {
      const data = JSON.parse(event.data) as Five9MessageData
      if (filter(data)) {
        clearTimeout(timeoutRef)
        ws.removeEventListener('message', listener)
        resolve(data)
      }
    }

    ws.addEventListener('message', listener)
  })

  return () => {
    timeoutRef = setTimeout(() => {
      ws.removeEventListener('message', listener)
      rejectPromise(new Error(timeoutMessage))
    }, timeout)

    return promise
  }
}

export const createFive9WebSocket = (
  host: string,
  farmId: string,
  socketNumber: number,
  onSocketDuplicated: () => void,
  onCallCreated: (call: CallInfo) => void,
  onCallUpdated: (call: CallInfo, reason?: string) => void,
  onCallDeleted: (call: CallInfo, reason?: string) => void,
  onStationUpdated: (station: StationInfo) => void,
  onPresenceUpdated: (presence: AgentPresenceInfo) => void,
  onOpen: (socket: WebSocket) => void,
  onClose: () => void,
  onPong: () => void
) => {
  if (socketNumber > FIVE9_NUMBER_OF_SOCKET_LIMIT) {
    return
  }

  // The auth has to come from the Cookie. There is no way to put it in manually in the websocket call
  let socket = new WebSocket(
    `wss://${host}/appsvcs/ws/${socketNumber}?farmId=${farmId}`
  )

  socket.onmessage = function (event: MessageEvent) {
    try {
      const data = JSON.parse(event.data) as Five9MessageData
      if (
        data.context.eventId === '1020' &&
        data.context.eventReason === 'Duplicate WebSocket Connection'
      ) {
        onSocketDuplicated()
        return
      }
      if (data.context.eventId === '1202' && data.payLoad === 'pong') {
        onPong()
        return
      }

      if (
        data.context.eventId === Five9EventType.CALL_DELETED ||
        data.context.eventId === Five9EventType.CALL_DELETED_ALT
      ) {
        onCallDeleted(data.payLoad, data.context.eventReason)
      } else if (
        data.context.eventId === Five9EventType.CALL_CREATED ||
        data.context.eventId === Five9EventType.CALL_CREATED_ALT
      ) {
        onCallCreated(data.payLoad)
      } else if (data.context.eventId === Five9EventType.CALL_UPDATED) {
        onCallUpdated(data.payLoad, data.context.eventReason)
      } else if (data.context.eventId === Five9EventType.STATION_UPDATED) {
        onStationUpdated(data.payLoad)
      } else if (data.context.eventId === Five9EventType.PRESENCE_UPDATED) {
        onPresenceUpdated(data.payLoad)
      }
    } catch (e) {}
  }

  socket.onerror = function (error) {
    console.log('WebSocket Error: ' + error)
  }

  const pingIntervalHandle = window.setInterval(function () {
    ping(socket)
  }, FIVE9_PING_INTERVAL)

  socket.onclose = function () {
    window.clearInterval(pingIntervalHandle)
    onClose()
  }

  socket.onopen = function () {
    onOpen(socket)
    ping(socket)
  }

  return socket
}
